import { useCallback, useEffect, useRef, useState } from 'react';
import type { AccountToastState } from '../../../components/account/AccountPremiumToast';
import type { CwyTransferRequest } from '../services/walletService';
import type { WalletLinkStatus } from './useSolanaWallets';
import { isActiveGrantRequest } from '../utils/transferProgress';
import {
  getTransferStatusBaseline,
  markTransferToastShown,
  resetTransferNotificationState,
  seedTransferNotificationBaseline,
  setTransferStatusBaseline,
  wasTransferToastShown,
} from '../utils/transferNotificationState';
import { toastForTransferStatusChange, toastForVaultDepositStatus } from '../utils/transferToastMessages';

const TOAST_DURATION_MS = 5500;

export { toastForVaultDepositStatus };

function isWalletCancellationMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('cancelled') ||
    lower.includes('canceled') ||
    lower.includes('declined') ||
    lower.includes('user rejected')
  );
}

export function useAccountToast() {
  const [toast, setToast] = useState<AccountToastState | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const closeToast = useCallback(() => {
    clearTimer();
    setToast(null);
  }, []);

  const showToast = useCallback((next: AccountToastState) => {
    clearTimer();
    setToast(next);
    timerRef.current = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  const showSuccess = useCallback(
    (title: string, message?: string) => showToast({ variant: 'success', title, message }),
    [showToast]
  );

  const showError = useCallback(
    (title: string, message?: string) => showToast({ variant: 'error', title, message }),
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string) => showToast({ variant: 'warning', title, message }),
    [showToast]
  );

  useEffect(() => () => clearTimer(), []);

  return { toast, showSuccess, showError, showWarning, closeToast };
}

function notifyTransferUpdate(
  req: CwyTransferRequest,
  prevStatus: string | undefined,
  handlers: {
    showSuccess: (title: string, message?: string) => void;
    showError: (title: string, message?: string) => void;
    showWarning: (title: string, message?: string) => void;
  }
): void {
  if (prevStatus === req.status) return;
  if (wasTransferToastShown(req.transfer_request_id, req.status)) return;

  const isPipelineExit =
    prevStatus === 'pending' ||
    prevStatus === 'processing' ||
    (prevStatus === undefined &&
      (req.status === 'completed' || req.status === 'rejected' || req.status === 'failed'));

  if (prevStatus === undefined && !isPipelineExit) {
    setTransferStatusBaseline(req.transfer_request_id, req.status);
    return;
  }

  if (prevStatus === undefined && isPipelineExit) {
    // Catch terminal updates that landed while the wallet modal was open or after a remount.
    toastForTransferStatusChange(req, 'pending', handlers);
    markTransferToastShown(req.transfer_request_id, req.status);
    setTransferStatusBaseline(req.transfer_request_id, req.status);
    return;
  }

  if (prevStatus !== undefined) {
    toastForTransferStatusChange(req, prevStatus, handlers);
    markTransferToastShown(req.transfer_request_id, req.status);
  }

  setTransferStatusBaseline(req.transfer_request_id, req.status);
}

export function useWalletTransferNotifications(options: {
  requests: CwyTransferRequest[];
  transfersLoading?: boolean;
  transfersEnabled?: boolean;
  linkStatus?: WalletLinkStatus;
  transferError: string | null;
  depositError: string | null;
  walletSuccessMessage: string | null;
  walletError: string | null;
  walletWarning?: string | null;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  clearWalletMessages: () => void;
}) {
  const {
    requests,
    transfersLoading = false,
    transfersEnabled = true,
    linkStatus = 'none',
    transferError,
    depositError,
    walletSuccessMessage,
    walletError,
    walletWarning,
    showSuccess,
    showError,
    showWarning,
    clearWalletMessages,
  } = options;

  const lastTransferErrorRef = useRef<string | null>(null);
  const lastDepositErrorRef = useRef<string | null>(null);
  const lastWalletSuccessRef = useRef<string | null>(null);
  const lastWalletErrorRef = useRef<string | null>(null);
  const lastWalletWarningRef = useRef<string | null>(null);
  const prevLinkStatusRef = useRef<WalletLinkStatus | null>(null);
  const pipelineRequestIdRef = useRef<string | null>(null);
  const linkStatusInitializedRef = useRef(false);

  const toastHandlers = { showSuccess, showError, showWarning };

  useEffect(() => {
    if (!transfersEnabled) {
      resetTransferNotificationState();
      pipelineRequestIdRef.current = null;
    }
  }, [transfersEnabled]);

  useEffect(() => {
    if (!transfersEnabled || transfersLoading) return;

    seedTransferNotificationBaseline(requests);

    for (const req of requests) {
      const prev = getTransferStatusBaseline(req.transfer_request_id);
      if (prev === req.status) continue;
      notifyTransferUpdate(req, prev, toastHandlers);
    }
  }, [requests, transfersEnabled, transfersLoading, showError, showSuccess, showWarning]);

  useEffect(() => {
    if (!transfersEnabled) return;

    const active = requests.find(isActiveGrantRequest);
    if (active) {
      pipelineRequestIdRef.current = active.transfer_request_id;
      return;
    }

    const trackedId = pipelineRequestIdRef.current;
    if (!trackedId) return;

    const terminal = requests.find((r) => r.transfer_request_id === trackedId);
    pipelineRequestIdRef.current = null;

    if (!terminal) return;
    if (!['completed', 'rejected', 'failed'].includes(terminal.status)) return;
    if (wasTransferToastShown(terminal.transfer_request_id, terminal.status)) return;

    const prev = getTransferStatusBaseline(terminal.transfer_request_id) ?? 'pending';
    toastForTransferStatusChange(terminal, prev, toastHandlers);
    markTransferToastShown(terminal.transfer_request_id, terminal.status);
    setTransferStatusBaseline(terminal.transfer_request_id, terminal.status);
  }, [requests, transfersEnabled, showError, showSuccess, showWarning]);

  useEffect(() => {
    if (!linkStatusInitializedRef.current) {
      prevLinkStatusRef.current = linkStatus;
      linkStatusInitializedRef.current = true;
      return;
    }

    const prev = prevLinkStatusRef.current;
    if (prev === linkStatus) return;

    if (prev === 'linked' && (linkStatus === 'none' || linkStatus === 'wallet_only')) {
      showWarning(
        'Wallet disconnected',
        'Your browser wallet closed. Reconnect the same wallet to sign transactions. Pending CWY requests stay on your account.'
      );
    } else if (prev === 'linked' && linkStatus === 'mismatch') {
      showWarning(
        'Wallet account changed',
        'The connected wallet does not match your primary wallet. Switch accounts in your extension or reconnect.'
      );
    } else if (prev === 'mismatch' && linkStatus === 'linked') {
      showSuccess('Wallet reconnected', 'Your primary wallet is connected again.');
    } else if ((prev === 'none' || prev === 'wallet_only') && linkStatus === 'linked') {
      showSuccess('Wallet connected', 'Your primary wallet is ready for CWY actions.');
    }

    prevLinkStatusRef.current = linkStatus;
  }, [linkStatus, showSuccess, showWarning]);

  useEffect(() => {
    if (!transferError) {
      lastTransferErrorRef.current = null;
      return;
    }
    if (transferError === lastTransferErrorRef.current) return;
    lastTransferErrorRef.current = transferError;
    showError('Request could not be submitted', transferError);
  }, [transferError, showError]);

  useEffect(() => {
    if (!depositError) {
      lastDepositErrorRef.current = null;
      return;
    }
    if (depositError === lastDepositErrorRef.current) return;
    lastDepositErrorRef.current = depositError;
    showError('Deposit failed', depositError);
  }, [depositError, showError]);

  useEffect(() => {
    if (!walletSuccessMessage) {
      lastWalletSuccessRef.current = null;
      return;
    }
    if (walletSuccessMessage === lastWalletSuccessRef.current) return;
    lastWalletSuccessRef.current = walletSuccessMessage;
    showSuccess('Wallet updated', walletSuccessMessage);
    clearWalletMessages();
  }, [walletSuccessMessage, showSuccess, clearWalletMessages]);

  useEffect(() => {
    if (!walletWarning) {
      lastWalletWarningRef.current = null;
      return;
    }
    if (walletWarning === lastWalletWarningRef.current) return;
    lastWalletWarningRef.current = walletWarning;
    showWarning('Wallet notice', walletWarning);
    clearWalletMessages();
  }, [walletWarning, showWarning, clearWalletMessages]);

  useEffect(() => {
    if (!walletError) {
      lastWalletErrorRef.current = null;
      return;
    }
    if (walletError === lastWalletErrorRef.current) return;
    lastWalletErrorRef.current = walletError;

    if (isWalletCancellationMessage(walletError)) {
      showWarning(
        'Wallet connection cancelled',
        'You closed the wallet before finishing. Your CWY request status is unchanged — check transaction logs for updates.'
      );
    } else if (walletError.toLowerCase().includes('switch to')) {
      showWarning('Wrong wallet account', walletError);
    } else {
      showError('Wallet action failed', walletError);
    }

    clearWalletMessages();
  }, [walletError, showError, showWarning, clearWalletMessages]);
}
